import React, {useState} from 'react';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {
  FamilyPlanMemberItem,
  useAddFamilyMember,
  useFamilyPlanMembers,
  useRemoveFamilyMember,
  useUpdateFamilyMember,
} from './requests/use-family-plan-members';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {ProgressCircle} from '@ui/progress/progress-circle';
import {GroupIcon} from '@ui/icons/material/Group';
import {CheckCircleIcon} from '@ui/icons/material/CheckCircle';
import {PersonAddIcon} from '@ui/icons/material/PersonAdd';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@ui/overlays/dialog/confirmation-dialog';

interface AccountPlanFamilyPanelProps {
  user?: any;
}

export function AccountPlanFamilyPanel(props?: AccountPlanFamilyPanelProps) {
  const {data, isLoading} = useFamilyPlanMembers();

  // Só exibe a seção se o usuário for titular com plano família ativo e elegível
  if (isLoading) {
    return null;
  }

  if (!data?.eligible || !data.is_owner || !data.plan || data.plan.max_members <= 1) {
    return null;
  }

  const {plan, members} = data;
  const maxDependents = plan.max_dependents;
  const usedSlots = members.length;

  return (
    <AccountSettingsPanel
      id="account-plan-family"
      title={<Trans message="Family Plan Members" />}
    >
      <div className="mb-20">
        <div className="flex items-center gap-10 text-sm text-muted">
          <GroupIcon size="sm" className="text-primary" />
          <span>
            <Trans
              message="Manage member accounts linked to your :plan plan."
              values={{plan: plan.name}}
            />
          </span>
        </div>
        <div className="mt-8 text-xs font-semibold text-muted">
          <Trans
            message=":used of :total member slots used"
            values={{used: usedSlots, total: maxDependents}}
          />
        </div>
      </div>

      <div className="space-y-16">
        {Array.from({length: maxDependents}).map((_, index) => {
          const member = members[index];
          return (
            <FamilySlotRow
              key={member ? member.id : `slot-${index}`}
              slotIndex={index}
              member={member}
            />
          );
        })}
      </div>
    </AccountSettingsPanel>
  );
}

interface FamilySlotRowProps {
  slotIndex: number;
  member?: FamilyPlanMemberItem;
}

function FamilySlotRow({slotIndex, member}: FamilySlotRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [emailInput, setEmailInput] = useState(member?.member_email || '');
  const [newEmail, setNewEmail] = useState('');

  const addMember = useAddFamilyMember();
  const updateMember = useUpdateFamilyMember();
  const removeMember = useRemoveFamilyMember();

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!member || !emailInput.trim()) return;

    updateMember.mutate(
      {id: member.id, email: emailInput.trim()},
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    addMember.mutate(
      {email: newEmail.trim()},
      {
        onSuccess: () => {
          setNewEmail('');
        },
      }
    );
  };

  if (member) {
    if (isEditing) {
      return (
        <form
          onSubmit={handleSaveEdit}
          className="rounded-panel border bg-paper p-16"
        >
          <div className="mb-8 text-xs font-semibold uppercase tracking-wider text-muted">
            <Trans message="Member :index" values={{index: slotIndex + 1}} />
          </div>
          <div className="flex flex-col gap-12 sm:flex-row sm:items-center">
            <TextField
              type="email"
              required
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="email@exemplo.com"
              className="flex-1"
              size="sm"
              disabled={updateMember.isPending}
            />
            <div className="flex items-center gap-8">
              <Button
                type="submit"
                variant="flat"
                color="primary"
                size="sm"
                disabled={updateMember.isPending || !emailInput.trim()}
              >
                <Trans message={updateMember.isPending ? 'Saving...' : 'Save'} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={updateMember.isPending}
                onClick={() => {
                  setEmailInput(member.member_email);
                  setIsEditing(false);
                }}
              >
                <Trans message="Cancel" />
              </Button>
            </div>
          </div>
        </form>
      );
    }

    return (
      <div className="flex flex-col justify-between gap-12 rounded-panel border bg-paper p-16 sm:flex-row sm:items-center">
        <div className="flex items-center gap-12">
          <CheckCircleIcon className="text-positive" size="sm" />
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted">
              <Trans message="Member :index" values={{index: slotIndex + 1}} />
            </div>
            <div className="text-sm font-medium text-main">
              {member.member_email}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => {
              setEmailInput(member.member_email);
              setIsEditing(true);
            }}
          >
            <Trans message="Edit" />
          </Button>

          <DialogTrigger
            type="modal"
            onClose={confirmed => {
              if (confirmed) {
                removeMember.mutate(member.id);
              }
            }}
          >
            <Button
              type="button"
              variant="outline"
              color="danger"
              size="xs"
              disabled={removeMember.isPending}
            >
              <Trans message="Remove" />
            </Button>
            <ConfirmationDialog
              title={<Trans message="Remove family member" />}
              body={
                <Trans
                  message="Are you sure you want to remove :email from your family plan? They will lose access to subscriber benefits immediately."
                  values={{email: member.member_email}}
                />
              }
              confirm={<Trans message="Remove" />}
              danger
            />
          </DialogTrigger>
        </div>
      </div>
    );
  }

  // Slot livre para adicionar dependente
  return (
    <form
      onSubmit={handleAddMember}
      className="rounded-panel border border-dashed bg-alt/40 p-16"
    >
      <div className="mb-8 flex items-center gap-6 text-xs font-semibold uppercase tracking-wider text-muted">
        <PersonAddIcon size="xs" />
        <Trans
          message="Member :index (Available slot)"
          values={{index: slotIndex + 1}}
        />
      </div>
      <div className="flex flex-col gap-12 sm:flex-row sm:items-center">
        <TextField
          type="email"
          required
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder="email@exemplo.com"
          className="flex-1"
          size="sm"
          disabled={addMember.isPending}
        />
        <Button
          type="submit"
          variant="flat"
          color="primary"
          size="sm"
          disabled={addMember.isPending || !newEmail.trim()}
          startIcon={
            addMember.isPending ? (
              <ProgressCircle isIndeterminate size="xs" />
            ) : (
              <PersonAddIcon size="xs" />
            )
          }
        >
          <Trans message={addMember.isPending ? 'Adding...' : 'Link member'} />
        </Button>
      </div>
    </form>
  );
}